import { StatDefinition } from 'app/shared/collections/StatDefinition';
import { Farm as Site } from 'app/agriculture/contexts/RequiredStartupProviders/FarmContext/farmSchema';

/**
 * Resource placeholder for testing
 * @constructor
 */
function ResourcePlaceholder() {
    this.toJSON = function () {
        return this;
    };
    this.save = function (v) {
        return v;
    };
    this.update = function (v) {
        return v;
    };
    this.remove = function (v) {
        return v;
    };
}

// Init to placeholder objects for testing
// Use 'any' typing to get around typescript errors
export let AssetResource: any = ResourcePlaceholder;
export let AssetTypeResource: any = ResourcePlaceholder;
let ConfigResource: any = ResourcePlaceholder;
let UserResource: any = ResourcePlaceholder;
export let IpDeviceResource: any = ResourcePlaceholder;
export let IpDeviceTypeResource: any = ResourcePlaceholder;
export let IpDeviceStatsResource: any = ResourcePlaceholder;
let RadioStatsResource: any = ResourcePlaceholder;
export let LocationStatsResource: any = ResourcePlaceholder;
let VisibleRadioResource: any = ResourcePlaceholder;
let SiteResource: Site;
export let TicketResource: any = ResourcePlaceholder;
export let CommentResource: any = ResourcePlaceholder;
let CustomResource: any = ResourcePlaceholder;
export let ViewResource: any = ResourcePlaceholder;
export let RadioResource: any = ResourcePlaceholder;
export let RadioTechnologyResource: any = ResourcePlaceholder;
export let SolarStatsResource: any = ResourcePlaceholder;
export let TriggerStatsResource: any = ResourcePlaceholder;
export let TriggerResource: any = ResourcePlaceholder;
let ConditionResource: any = ResourcePlaceholder;
export let ZoneResource: any = ResourcePlaceholder;
export let ZoneTypeResource: any = ResourcePlaceholder;
export let ZoneHistoryResource: any = ResourcePlaceholder;
export let TagResource: any = ResourcePlaceholder;
export let AuditLogResource: any = ResourcePlaceholder;
let StateResource: any = ResourcePlaceholder;
let AssetStateValueResource: any = ResourcePlaceholder;
let IPDeviceStateValueResource: any = ResourcePlaceholder;
let IpDeviceLogResource: any = ResourcePlaceholder;
export let SystemTypeResource: any = ResourcePlaceholder;
let SimpleSystemResource: any = ResourcePlaceholder;
export let CensoredSystemResource: any = ResourcePlaceholder;
let SystemHealthResource: any = ResourcePlaceholder;
export let IntegrationResource: any = ResourcePlaceholder;
export let AggregateResource: any = ResourcePlaceholder;
export let FileResource: any = ResourcePlaceholder;
let LicensingResource: any = ResourcePlaceholder;
export let RFPlanResource: any = ResourcePlaceholder;
export let RFTransmitterResource: any = ResourcePlaceholder;
export let RFAntFileResource: any = ResourcePlaceholder;
export let DynamicStatSchemaResource: any = ResourcePlaceholder;
let ApiSettingsResource: any = ResourcePlaceholder;
let AuthResource: any = ResourcePlaceholder;
export let DashboardTabResource: any = ResourcePlaceholder;
export let DashboardResource: any = ResourcePlaceholder;
export let ReportResource: any = ResourcePlaceholder;
export let CustomIMSConfigResource: any = ResourcePlaceholder;
let ImsConfigItemsResource: any = ResourcePlaceholder;
let SMTPConfigResource: any = ResourcePlaceholder;
let LDAPConfigResource: any = ResourcePlaceholder;
export let AssetTemplateResource: any = ResourcePlaceholder;
export let AssetTemplateIntegrationResource: any = ResourcePlaceholder;
export let IpDeviceTemplateResource: any = ResourcePlaceholder;
let ServiceNowConfigResource: any = ResourcePlaceholder;

/**
 * @ngdoc service
 * @name ims.shared.data.service:AggregateResource
 * @description
 * Returns a $resource that retrieves aggregated stats data.
 *
 * - ipDeviceStats: Retrieves an aggregated array of ip_device_stats values when provided with
 * a timestamp__lt, timestamp__lte, timestamp__gt, timestamp__gte, group_size (in seconds) or an asset_id (all optional).
 * Data is returned for all child ip devices of the specified asset
 * combined, or if no specified asset, an aggregate of all ip devices.
 * /aggregates/ip_device_stats/
 *
 * - radioStats: Retrieves an array containing ip_device_id, ip_device names and grouped (averaged)
 * radio_stats data when provided with a timestamp__lt, timestamp__lte, timestamp__gt, timestamp__gte,
 * group_size (in seconds), or asset_id (all optional). Data is returned for
 * all child ip devices of the specified asset.
 * /aggregates/radio_stats_by_ip_device/
 *
 *  * - ipDeviceStatsHourly: Retrieves an aggregated array of ip_device_stats values when provided with
 * a timestamp__lt, timestamp__lte, timestamp__gt, timestamp__gte, group_size (in seconds) or an asset_id (all optional).
 * Data is returned for all child ip devices of the specified asset
 * combined, or if no specified asset, an aggregate of all ip devices. Max granularity is 1h. Used for large time-frames.
 * /aggregates/ip_device_stats_hourly/
 *
 * - radioStatsHourly: Retrieves an array containing ip_device_id, ip_device names and grouped (averaged)
 * radio_stats data when provided with a timestamp__lt, timestamp__lte, timestamp__gt, timestamp__gte,
 * group_size (in seconds), or asset_id (all optional). Data is returned for
 * all child ip devices of the specified asset. Max granularity is 1h. Used for large time-frames.
 * /aggregates/radio_stats_by_ip_device_hourly/
 *
 * **Query Parameters:**
 * - **timestamp__gte**: OPTIONAL, Filter out stats after and including this (string, format: 2015-10-20T13:30:30).
 * If left out, defaults to 15s in the past.
 * - **timestamp__gt**: OPTIONAL, Filter out stats after this (string, format: 2015-10-20T13:30:30).
 * If left out, defaults to current time.
 * - **timestamp__lte**: OPTIONAL, Filter out stats before and including this (string, format: 2015-10-20T13:30:30).
 * If left out, defaults to 15s in the past.
 * - **timestamp__lt**: OPTIONAL, Filter out stats before this (string, format: 2015-10-20T13:30:30).
 * If left out, defaults to current time.
 * - **group_size**: The number of seconds to group data by. 0 means all time. (integer)
 * - **asset_id**: The id of a particular asset if you wish to filter by asset. (integer)
 * - **asset_type_id**: The id of a particular asset type if you wish to filter by asset type. (integer)
 * - **ip_device_id**: The id of a particular ip device if you wish to filter by ip device. (integer)
 * - **group_by**: The field you wish to group the data by: ip_device, or function. (string)
 *
 */

// The base aggregate resource actions
function createAggResourceActions(apiUrlRootDashboard: string) {
    if (!apiUrlRootDashboard) {
        throw new Error(`apiUrlRootDashboard cannot be falsey`);
    }

    return {
        ipDeviceStats: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/ip_device_stats/',
            cache: true,
        },
        radioStats: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/radio_stats/',
            cache: true,
        },
        ipDeviceStatsHourly: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/ip_device_stats_hourly/',
            cache: true,
        },
        radioStatsHourly: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/radio_stats_hourly/',
            cache: true,
        },
        ipDeviceStats5m: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/ip_device_stats_5m/',
            cache: true,
        },
        radioStats5m: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/radio_stats_5m/',
            cache: true,
        },
        solarControllerStats: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/solar_controller_stats/',
            cache: true,
        },
        solarControllerStatsHourly: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/solar_controller_stats_hourly/',
            cache: true,
        },
        hardwareStats: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/hardware_stats/',
            cache: true,
        },
        hardwareStatsHourly: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/hardware_stats_hourly/',
            cache: true,
        },
        connectionsAsClient: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/connections_as_client/',
            cache: true,
        },
        connectionsAsClientHourly: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/connections_as_client_hourly/',
            cache: true,
        },
        connectionsAsClient5m: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/connections_as_client_5m/',
            cache: true,
        },
        connectionsAsAP: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/connections_as_ap/',
            cache: true,
        },
        connectionsAsAPHourly: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/connections_as_ap_hourly/',
            cache: true,
        },
        connectionsAsAP5m: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/connections_as_ap_5m/',
            cache: true,
        },
        processStats: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/process_stats/',
            cache: true,
        },
        processStatsHourly: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/process_stats_hourly/',
            cache: true,
        },
        storageStats: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/storage_stats/',
            cache: true,
        },
        storageStatsHourly: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/storage_stats_hourly/',
            cache: true,
        },
        connectionDistanceAsClient: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/connection_distance_as_client/',
            cache: true,
        },
        connectionDistanceAsAp: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/connection_distance_as_ap/',
            cache: true,
        },
        triggerStats: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/trigger_stats/',
            cache: true,
        },
        triggerStatsHourly: {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + 'aggregates/trigger_stats_hourly/',
            cache: true,
        },
    };
}

export const WITH_LOCATION_ENDPOINT_FUNC = 'withLocation';
export const DISTINCT_SUBGROUP_VALUES_KEY = 'distinctSubgroupValues';

export function createWithLocationEndpoint(apiUrlRootStandard: string, baseEndPoint: string) {
    if (!apiUrlRootStandard) {
        throw new Error(`apiUrlRootStandard cannot be falsey`);
    }

    return {
        method: 'POST',
        url: `${apiUrlRootStandard}${baseEndPoint}/with_location/`,
        isArray: true,
    };
}

export function setupResources($resource, apiUrlRootStandard, apiUrlRootDashboard) {
    if (!apiUrlRootStandard) {
        throw new Error(`apiUrlRootStandard cannot be falsey`);
    }
    if (!apiUrlRootDashboard) {
        throw new Error(`apiUrlRootDashboard cannot be falsey`);
    }

    TriggerStatsResource = $resource(apiUrlRootStandard + 'trigger_stats/:id', { id: '@id' });

    // Do a simple test whether the first resource object can be constructed.
    // We do this because if a unit test calls setupResources with a mocked $resource that produces
    // something non-constructable, unrelated tests will intermittently fail.
    if (!TriggerResource.prototype) {
        throw new Error(
            '$resource() does not produce a constructable. Is your unit test set up properly?',
        );
    }

    /**
     * @ngdoc service
     * @name ims.shared.data.service:AssetResource
     * @description
     * Returns a $resource that retrieves an array of assets from the API.
     *
     * **API endpoint:**  /assets/
     *
     * **Extra Actions:**
     * - update: Updates the asset via patch
     *
     * - latestStats: Retrieves an array of the latest IPDevice and Radio stats
     * (last 24s) by asset from the API.
     *
     * - stats: Retrieves an array of IPDevice and Radio stats by asset from the API.
     * A time-frame can be specified via the query params.
     * **Query Parameters:**
     * - **timestamp__lt**: OPTIONAL, Filter out stats before this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp__gt**: OPTIONAL, Filter out stats after this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     * - **timestamp__lte**: OPTIONAL, Filter out stats before and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp__gte**: OPTIONAL, Filter out stats after and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     *
     * - children: Retrieves an array of assets and their ip-device and radio
     * children from the API.
     *
     */
    AssetResource = $resource(
        apiUrlRootStandard + 'assets/:id/',
        { id: '@id' },
        {
            createWithStat: {
                method: 'POST',
                url: apiUrlRootStandard + 'assets/create_with_stat/',
            },
            save: {
                method: 'POST',
                url: apiUrlRootStandard + 'assets/create_with_location/',
            },
            update: { method: 'PATCH' },
            updateBulk: {
                method: 'PATCH',
                url: apiUrlRootStandard + 'assets/',
                isArray: true,
            },
            applyBulkChanges: {
                method: 'POST',
                url: apiUrlRootStandard + 'assets/apply_bulk_changes/',
                isArray: false,
            },
            deleteBulk: {
                method: 'DELETE',
                url: apiUrlRootStandard + 'assets/',
                hasBody: true,
                headers: { 'Content-Type': 'application/json;charset=UTF-8' },
            },
            latestStats: {
                method: 'GET',
                url: apiUrlRootStandard + 'assets/latest_stats/',
                isArray: true,
            },
            stats: {
                method: 'GET',
                url: apiUrlRootStandard + 'assets/stats/',
                isArray: true,
            },
            children: {
                method: 'GET',
                url: apiUrlRootStandard + 'assets/children/',
                isArray: true,
            },
            commentCounts: {
                method: 'GET',
                url: apiUrlRootStandard + 'assets/comment_counts/',
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:AssetTypeResource
     * @description
     * Returns a $resource that retrieves an array of asset types from the API.
     *
     */
    AssetTypeResource = $resource(
        apiUrlRootStandard + 'asset_types/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:ConfigResource
     * @description
     * Returns a $resource that retrieves an array of asset types from the API.
     *
     */
    ConfigResource = $resource(
        apiUrlRootStandard + 'config/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:UserResource
     * @description
     * Returns a $resource that retrieves an array of users from the API.
     *
     */
    UserResource = $resource(
        apiUrlRootStandard + 'users/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    AuthResource = $resource(
        apiUrlRootStandard + 'auth/',
        {},
        {
            changePassword: {
                method: 'POST',
                url: apiUrlRootStandard + 'auth/password/change/',
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:IpDeviceResource
     * @description
     * Returns a $resource that retrieves an array of ip devices from the API.
     * **API endpoint:**  /ip_devices/
     *
     */
    IpDeviceResource = $resource(
        apiUrlRootStandard + 'ip_devices/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            updateBulk: {
                method: 'PATCH',
                url: apiUrlRootStandard + 'ip_devices/',
                isArray: true,
            },
            applyBulkChanges: {
                method: 'POST',
                url: apiUrlRootStandard + 'ip_devices/apply_bulk_changes/',
                isArray: false,
            },
            deleteBulk: {
                method: 'DELETE',
                url: apiUrlRootStandard + 'ip_devices/',
                hasBody: true,
                headers: { 'Content-Type': 'application/json;charset=UTF-8' },
            },
            commentCounts: {
                method: 'GET',
                url: apiUrlRootStandard + 'ip_devices/comment_counts/',
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:IpDeviceTypeResource
     * @description
     * Returns a $resource that retrieves an array of ip device types from the API.
     * **API endpoint:**  /ip_device_types/
     *
     */
    IpDeviceTypeResource = $resource(
        apiUrlRootStandard + 'ip_device_types/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:IpDeviceStatsResource
     * @description
     * Returns a $resource that retrieves an array of IPDevice stats, Radio stats, and visible ip-devices from the API.
     * A time-frame can be specified via the query params.
     *
     * **API endpoint:**  /ip_device_stats/children/
     *
     * **Query Parameters:**
     * - **timestamp__lt**: OPTIONAL, Filter out stats before this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp__gt**: OPTIONAL, Filter out stats after this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     * - **timestamp__lte**: OPTIONAL, Filter out stats before and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp__gte**: OPTIONAL, Filter out stats after and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     *
     */
    IpDeviceStatsResource = $resource(
        apiUrlRootStandard + 'ip_device_stats/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            children: {
                method: 'GET',
                url: apiUrlRootStandard + 'ip_device_stats/children/',
                isArray: true,
            },
            [WITH_LOCATION_ENDPOINT_FUNC]: createWithLocationEndpoint(
                apiUrlRootStandard,
                'ip_device_stats',
            ),
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:RadioStatsResource
     * @description
     * Returns a $resource that retrieves an array of RadioStats and visible radios.
     * A time-frame can be specified via the query params.
     *
     * **API endpoint:**  /radio_stats
     *
     * **Query Parameters:**
     * - **timestamp_lt**: OPTIONAL, Filter out stats before this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp_gt**: OPTIONAL, Filter out stats after this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     * - **timestamp_lte**: OPTIONAL, Filter out stats before and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp_gte**: OPTIONAL, Filter out stats after and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     *
     */
    RadioStatsResource = $resource(
        apiUrlRootStandard + 'radio_stats/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:RadioStatsResource
     * @description
     * Returns a $resource that retrieves an array of RadioStats and visible radios.
     * A time-frame can be specified via the query params.
     *
     * **API endpoint:**  /location_stats
     *
     * **Query Parameters:**
     * - **timestamp_lt**: OPTIONAL, Filter out stats before this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp_gt**: OPTIONAL, Filter out stats after this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     * - **timestamp_lte**: OPTIONAL, Filter out stats before and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp_gte**: OPTIONAL, Filter out stats after and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     *
     */
    LocationStatsResource = $resource(
        apiUrlRootStandard + 'location_stats/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            latest_by_asset: {
                method: 'GET',
                url: apiUrlRootStandard + 'location_stats/latest_by_asset/',
                isArray: true,
            },
            // yes, the location stats endpoint also has a /with_location sub-endpoint; it's just
            //  here to match the api return value interface; I suppose you can think of the /with_location endpoints
            //  as more or /heatmap_ready_data endpoints
            [WITH_LOCATION_ENDPOINT_FUNC]: createWithLocationEndpoint(
                apiUrlRootStandard,
                'location_stats',
            ),
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:VisibleRadio Resource
     * @description
     * Returns a $resource that retrieves an array of VisibleRadios and visible radios.
     * A time-frame can be specified via the query params.
     *
     * **API endpoint:**  /radio_stats
     *
     * **Query Parameters:**
     * - **timestamp_lt**: OPTIONAL, Filter out stats before this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp_gt**: OPTIONAL, Filter out stats after this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     * - **timestamp_lte**: OPTIONAL, Filter out stats before and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to 15s in the past.
     * - **timestamp_gte**: OPTIONAL, Filter out stats after and including this (string, format: 2015-10-20T13:30:30).
     * If left out, defaults to current time.
     *
     */
    VisibleRadioResource = $resource(
        apiUrlRootStandard + 'visible_radios/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:SiteResource
     * @description
     * Returns a $resource that retrieves an array of sites.
     * **API endpoint:**  /api/sites/
     *
     */
    SiteResource = $resource(
        apiUrlRootStandard + 'sites/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:TicketResource
     * @description
     * Returns a $resource that retrieves an array of tickets.
     * **API endpoint:**  /api/tickets/
     *
     */
    TicketResource = $resource(
        apiUrlRootStandard + 'tickets/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            count: { method: 'GET', url: apiUrlRootStandard + 'tickets/count/' },
            commentCounts: {
                method: 'GET',
                url: apiUrlRootStandard + 'tickets/comment_counts/',
            },
            activityCounts: {
                method: 'GET',
                url: apiUrlRootStandard + 'tickets/activity_counts/',
            },
            ticketCounts: {
                method: 'GET',
                url: apiUrlRootStandard + 'tickets/ticket_counts/',
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:CommentResource
     * @description
     * Returns a $resource that retrieves an array of comments.
     * **API endpoint:**  /api/comments/
     *
     */
    CommentResource = $resource(
        apiUrlRootStandard + 'comments/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            count: { method: 'GET', url: apiUrlRootStandard + 'comments/count/' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:CommentResource
     * @description
     * Returns a $resource that retrieves an array of comments.
     * **API endpoint:**  /api/comments/
     *
     */
    CustomResource = $resource(
        apiUrlRootStandard + 'custom_stats/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:ViewResource
     * @description
     * Returns a $resource that retrieves an array of Views.
     * **API endpoint:**  /api/views/
     *
     */
    ViewResource = $resource(
        apiUrlRootStandard + 'views/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:RadioResource
     * @description
     * Returns a $resource that retrieves an array of radios.
     * **API endpoint:**  /api/radios/
     *
     */
    RadioResource = $resource(
        apiUrlRootStandard + 'radios/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:RadioTechnologyResource
     * @description
     * Returns a $resource that retrieves an array of radio technologies.
     * **API endpoint:**  /api/radio_technologies/
     *
     */
    RadioTechnologyResource = $resource(
        apiUrlRootStandard + 'radio_technologies/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:SolarStatsResource
     * @description
     * Returns a $resource that retrieves an array of radios.
     * **API endpoint:**  /api/solar_controller_stats/
     *
     */
    SolarStatsResource = $resource(
        apiUrlRootStandard + 'solar_controller_stats/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            [WITH_LOCATION_ENDPOINT_FUNC]: createWithLocationEndpoint(
                apiUrlRootStandard,
                'solar_controller_stats',
            ),
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:TriggerResource
     * @description
     * Returns a $resource that retrieves an array of triggers.
     * **API endpoint:**  /api/triggers/
     *
     */
    TriggerResource = $resource(
        apiUrlRootStandard + 'triggers/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            commentCounts: {
                method: 'GET',
                url: apiUrlRootStandard + 'triggers/comment_counts/',
            },
            lastTripped: {
                method: 'GET',
                url: apiUrlRootStandard + 'triggers/last_tripped/',
            },
            applyAction: {
                method: 'POST',
                url: apiUrlRootStandard + 'triggers/apply_action/',
            },
            activityCounts: {
                method: 'GET',
                url: apiUrlRootStandard + 'triggers/activity_counts/',
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:ConditionResource
     * @description
     * Returns a $resource that retrieves an array of conditions.
     * **API endpoint:**  /api/conditions/
     *
     */
    ConditionResource = $resource(
        apiUrlRootStandard + 'conditions/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:ZoneResource
     * @description
     * Returns a $resource that retrieves zones.
     * **API endpoint:**  /api/zones/
     *
     */
    ZoneResource = $resource(
        apiUrlRootStandard + 'zones/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            save: { method: 'POST', url: apiUrlRootStandard + 'zones/create_with_history/' },
            commentCounts: {
                method: 'GET',
                url: apiUrlRootStandard + 'zones/comment_counts/',
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:ZoneResource
     * @description
     * Returns a $resource that retrieves zones.
     * **API endpoint:**  /api/zone_types/
     *
     */
    ZoneTypeResource = $resource(
        apiUrlRootStandard + 'zone_types/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:ZoneResource
     * @description
     * Returns a $resource that retrieves zones.
     * **API endpoint:**  /api/zone_types/
     *
     */
    ZoneHistoryResource = $resource(
        apiUrlRootStandard + 'zone_history/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    TagResource = $resource(
        apiUrlRootStandard + 'tags/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    AuditLogResource = $resource(
        apiUrlRootStandard + 'auditlog_combined/:id/',
        { id: '@id' },
        {
            count: { method: 'GET', url: apiUrlRootStandard + 'auditlog_combined/count/' },
        },
    );

    FileResource = $resource(
        apiUrlRootStandard + 'files/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            count: { method: 'GET', url: apiUrlRootStandard + 'file/count/' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:StateResource
     * @description
     * Returns a $resource that retrieves an array of tickets.
     * **API endpoint:**  /api/states/
     *
     */
    StateResource = $resource(
        apiUrlRootStandard + 'states/:id',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:AssetStateValueResource
     * @description
     * Returns a $resource that retrieves an array of state values for assets.
     * **API endpoint:**  /api/asset_state_values/
     *
     */
    AssetStateValueResource = $resource(
        apiUrlRootStandard + 'asset_state_values/:id/',
        { id: '@id' },
        {
            latestStates: {
                method: 'GET',
                url: apiUrlRootStandard + 'asset_state_values/latest_states/',
                isArray: true,
            },
            getUniqueValues: {
                method: 'GET',
                url: apiUrlRootStandard + 'asset_state_values/unique_values/',
                isArray: true,
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:IPDeviceStateValueResource
     * @description
     * Returns a $resource that retrieves an array of state values for ip devices.
     * **API endpoint:**  /api/ip_device_state_values/
     *
     */
    IPDeviceStateValueResource = $resource(
        apiUrlRootStandard + 'ip_device_state_values/:id/',
        { id: '@id' },
        {
            latestStates: {
                method: 'GET',
                url: apiUrlRootStandard + 'ip_device_state_values/latest_states/',
                isArray: true,
            },
            getUniqueValues: {
                method: 'GET',
                url: apiUrlRootStandard + 'ip_device_state_values/unique_values/',
                isArray: true,
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:IpDeviceLogResource
     * @description
     * Returns a $resource that retrieves an array of ip device logs.
     * **API endpoint:**  /api/ip_device_logs/
     *
     */
    IpDeviceLogResource = $resource(
        apiUrlRootStandard + 'ip_device_logs/:id/',
        { id: '@id' },
        {
            getUniqueLogLevels: {
                method: 'GET',
                url: apiUrlRootStandard + 'ip_device_logs/unique_log_levels/',
                isArray: true,
            },
            getUniqueServiceNames: {
                method: 'GET',
                url: apiUrlRootStandard + 'ip_device_logs/unique_service_names/',
                isArray: true,
            },
            getUniqueLogFileNames: {
                method: 'GET',
                url: apiUrlRootStandard + 'ip_device_logs/unique_log_file_names/',
                isArray: true,
            },
            latestByIpDevice: {
                method: 'GET',
                url: apiUrlRootStandard + 'ip_device_logs/latest_by_ip_device/',
                isArray: true,
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:SimpleSystemResource
     * @description
     * Returns a $resource that retrieves an array of simple systems.
     * **API endpoint:**  /api/simple_systems/
     *
     */
    SimpleSystemResource = $resource(
        apiUrlRootStandard + 'simple_systems/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:CensoredSystemResource
     * @description
     * Returns a $resource that retrieves an array of censored systems.
     * **API endpoint:**  /api/censored_systems/
     *
     */
    CensoredSystemResource = $resource(
        apiUrlRootStandard + 'censored_systems/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            updateBulk: {
                method: 'PATCH',
                url: apiUrlRootStandard + 'censored_systems/',
                isArray: true,
            },
            activityCounts: {
                method: 'GET',
                url: apiUrlRootStandard + 'censored_systems/activity_counts/',
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:SystemTypeResource
     * @description
     * Returns a $resource that retrieves an array of system types.
     * **API endpoint:**  /api/system_types/
     *
     */
    SystemTypeResource = $resource(
        apiUrlRootStandard + 'system_types/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:IntegrationResource
     * @description
     * Returns a $resource that retrieves an array of integrations.
     * **Integrations endpoint:**  /api/integrations/
     *
     */
    IntegrationResource = $resource(
        apiUrlRootStandard + 'integrations/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:RFTransmitterResource
     * @description
     * Returns a $resource that retrieves an array of RFTransmitters.
     * **RFTransmitter endpoint:**  /api/rf_transmitters/
     *
     */
    RFTransmitterResource = $resource(
        apiUrlRootStandard + 'rf_transmitters/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            saveBulk: {
                method: 'POST',
                isArray: true,
            },
            deleteBulk: {
                method: 'DELETE',
                url: apiUrlRootStandard + 'rf_transmitters/',
                hasBody: true,
                headers: { 'Content-Type': 'application/json;charset=UTF-8' },
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:RFPlanResource
     * @description
     * Returns a $resource that retrieves an array of RFPlans.
     * **RFPlan endpoint:**  /api/rf_plans/
     *
     */
    RFPlanResource = $resource(
        apiUrlRootStandard + 'rf_plans/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            // Creates a new RF Plan and also creates a template RF Transmitter as its child
            createWithTemplateRfTransmitter: {
                method: 'POST',
                url: apiUrlRootStandard + 'rf_plans/create_with_template_rf_transmitter/',
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:RFAntFileResource
     * @description
     * Returns a $resource that retrieves an array of RFAntFiles.
     * **RFAntFile endpoint:**  /api/rf_ant_files/
     *
     */
    RFAntFileResource = $resource(
        apiUrlRootStandard + 'rf_ant_files/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    AggregateResource = $resource(
        apiUrlRootStandard + 'aggregates/',
        {},
        createAggResourceActions(apiUrlRootDashboard),
    );

    DynamicStatSchemaResource = $resource(
        apiUrlRootStandard + 'dynamic_stat_schemas/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    LicensingResource = $resource(
        apiUrlRootStandard + 'licensing/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    ApiSettingsResource = $resource(
        apiUrlRootStandard + 'api_settings/',
        {},
        {
            query: { method: 'GET', isArray: false, cache: true },
        },
    );

    SystemHealthResource = $resource(
        apiUrlRootStandard + 'systems_health/',
        {},
        {
            query: { method: 'GET', isArray: true },
        },
    );

    DashboardTabResource = $resource(
        apiUrlRootStandard + 'dashboard_tabs/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            myAccessibleDashboardTabs: {
                method: 'GET',
                url: apiUrlRootStandard + 'dashboard_tabs/my_accessible_dashboard_tabs/',
                isArray: true,
            },
        },
    );

    DashboardResource = $resource(
        apiUrlRootStandard + 'dashboards/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            myDashboards: {
                method: 'GET',
                url: apiUrlRootStandard + 'dashboards/my_dashboards/',
                isArray: true, // This will return an array of size 1
            },
        },
    );

    ReportResource = $resource(
        apiUrlRootStandard + 'reports/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            applyAction: {
                method: 'POST',
                url: apiUrlRootStandard + 'reports/apply_action/',
            },
        },
    );
    CustomIMSConfigResource = $resource(
        apiUrlRootStandard + 'frontend_config/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
            getImageryLayerDefinitions: {
                method: 'GET',
                url: apiUrlRootStandard + 'frontend_config/get_imagery_layer_definitions/',
            },
        },
    );

    ImsConfigItemsResource = $resource(
        apiUrlRootStandard + 'frontend_config/:id/',
        { id: '@id' },
        {
            update: { method: 'POST', url: apiUrlRootStandard + 'frontend_config/partial_update/' },
            testLdap: { method: 'POST', url: apiUrlRootStandard + 'frontend_config/test/ldap/' },
            testSmtp: { method: 'POST', url: apiUrlRootStandard + 'frontend_config/test/smtp/' },
            testServiceNow: {
                method: 'POST',
                url: apiUrlRootStandard + 'frontend_config/test/servicenow/',
            },
        },
    );

    SMTPConfigResource = $resource(
        apiUrlRootStandard + 'frontend_config/:id/',
        { id: '@id' },
        {
            get: {
                method: 'GET',
                url: apiUrlRootStandard + 'frontend_config/partial_get/?section=messengerService',
            },
        },
    );

    LDAPConfigResource = $resource(
        apiUrlRootStandard + 'frontend_config/:id/',
        { id: '@id' },
        {
            get: {
                method: 'GET',
                url: apiUrlRootStandard + 'frontend_config/partial_get/?section=ldap',
            },
        },
    );

    ServiceNowConfigResource = $resource(
        apiUrlRootStandard + 'frontend_config/:id/',
        { id: '@id' },
        {
            get: {
                method: 'GET',
                url: apiUrlRootStandard + 'frontend_config/partial_get/?section=serviceNow',
            },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:AssetTemplateResource
     * @description
     * Returns a $resource that retrieves an array of asset templates.
     * **Asset Templates endpoint:**  /api/asset_templates/
     *
     */
    AssetTemplateResource = $resource(
        apiUrlRootStandard + 'asset_templates/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:IpDeviceTemplateResource
     * @description
     * Returns a $resource that retrieves an array of ip device templates.
     * **IP Device Templates endpoint:**  /api/ip_device_templates/
     *
     */
    IpDeviceTemplateResource = $resource(
        apiUrlRootStandard + 'ip_device_templates/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );

    /**
     * @ngdoc service
     * @name ims.shared.data.service:AssetTemplateIntegrationResource
     * @description
     * Returns a $resource that retrieves an array of asset template integrations.
     * **Asset Template Integrations endpoint:**  /api/asset_template_integrations/
     *
     */
    AssetTemplateIntegrationResource = $resource(
        apiUrlRootStandard + 'asset_template_integrations/:id/',
        { id: '@id' },
        {
            update: { method: 'PATCH' },
        },
    );
}

// Add dynamicStatSchema actions to the aggregate endpoint
export function createDynamicStatAggResources(
    $resource,
    statDefinitions: StatDefinition[],
    apiUrlRootDashboard: string,
) {
    if (!apiUrlRootDashboard) {
        throw new Error(`apiUrlRootDashboard cannot be falsey`);
    }

    const dynamicStatAggregateActions = {};
    const endpoints = [];

    // Build list of endpoints
    for (const dynamicStatSchema of statDefinitions) {
        endpoints.push(dynamicStatSchema.name);

        const rollups = dynamicStatSchema.rollups;
        const timeSliceMinsArray = rollups.map((rollup) => rollup.time_slice_mins);

        for (const time_slice_mins of timeSliceMinsArray) {
            endpoints.push(`${dynamicStatSchema.name}_${time_slice_mins}m`);
        }
    }

    endpoints.map(function (endpoint) {
        dynamicStatAggregateActions[endpoint] = {
            method: 'GET',
            isArray: true,
            url: apiUrlRootDashboard + `aggregates/${endpoint}/`,
            cache: true,
        };
    });

    AggregateResource = $resource(
        apiUrlRootDashboard + 'aggregates/',
        {},
        Object.assign(createAggResourceActions(apiUrlRootDashboard), dynamicStatAggregateActions),
    );
}
